/**
 * Per-tab isolation of SAL's permissions cache.
 *
 * The active account in Admin2 is per-tab (driven by the URL ?aid= and the per-tab X-Admin2-Account
 * header). SAL's @sal/authentication.react, however, persists the user's permissions in
 * `localStorage` under the key "salauth", and `userHasPermission()` reads that key live on every
 * render. `localStorage` is shared across all tabs of the same origin, so a second tab on a
 * different account would overwrite the first tab's permissions and its route guards would then
 * evaluate the wrong account — bouncing in-app navigations to /NotFound.
 *
 * SAL exposes a `localStorageKey` setting, but @sal/portal's PortalFramework hardcodes the auth
 * settings it passes to the auth context and never forwards it, so we cannot configure a per-tab
 * key without modifying the library. Instead we redirect just that one key to `sessionStorage`,
 * which is inherently per-tab (and auto-clears when the tab closes, so nothing accumulates). This
 * mirrors the existing `installAccountHeaderInterceptor()` fetch patch: a boot-time interceptor
 * that makes a shared browser API behave per-tab.
 *
 * Permissions are repopulated on every load anyway (SAL's enableTokenRefreshOnLoad Refresh plus
 * AccountProvider's ActivePermissions fetch), so moving this cache to ephemeral per-tab storage
 * costs nothing functionally.
 */

const ISOLATED_KEY = "salauth";
const INSTALLED_FLAG = "__admin2PerTabAuthStorageInstalled";

export function installPerTabAuthStorageIsolation(): void {
	const win = window as unknown as Record<string, unknown>;
	if (win[INSTALLED_FLAG]) {
		return; // idempotent — guard against double-install (e.g. HMR)
	}

	const descriptor = Object.getOwnPropertyDescriptor(window, "localStorage");
	if (!descriptor?.configurable) {
		// Can't redefine localStorage on this platform; leave it shared rather than crash.
		console.warn(
			"Per-tab auth storage isolation unavailable: window.localStorage is not configurable."
		);
		return;
	}

	const realLocalStorage = window.localStorage;
	const tabStorage = window.sessionStorage;

	// Purge any salauth left in real localStorage. Before this interceptor existed SAL wrote
	// permissions there (shared across tabs and across users on this machine); a different tab on the
	// new bundle on the same machine can also have written one before this ran. That stale copy is no
	// longer the source of truth — sessionStorage is — and must not linger: it misleads anything that
	// reads raw localStorage (e.g. DevTools), leaks the previous user's identity, and would be read by
	// SAL itself on any platform where the proxy below fails to install. Session identity is
	// re-established from cookies via SAL's Refresh-on-load regardless, so removing it is safe.
	realLocalStorage.removeItem(ISOLATED_KEY);

	const handler: ProxyHandler<Storage> = {
		get(target, prop, receiver) {
			if (prop === "getItem") {
				return (key: string) =>
					key === ISOLATED_KEY ? tabStorage.getItem(key) : target.getItem(key);
			}
			if (prop === "setItem") {
				return (key: string, value: string) =>
					key === ISOLATED_KEY ? tabStorage.setItem(key, value) : target.setItem(key, value);
			}
			if (prop === "removeItem") {
				return (key: string) =>
					key === ISOLATED_KEY ? tabStorage.removeItem(key) : target.removeItem(key);
			}
			// Named-property (bracket/dot) read, e.g. localStorage["salauth"]. Native bracket access
			// yields `undefined` for a missing key, so normalise sessionStorage's `null` to match.
			if (prop === ISOLATED_KEY) {
				const value = tabStorage.getItem(ISOLATED_KEY);
				return value === null ? undefined : value;
			}
			const value = Reflect.get(target, prop, target);
			// Bind methods (length/key/clear/...) to the real Storage to avoid "Illegal invocation".
			return typeof value === "function"
				? (value as (...a: unknown[]) => unknown).bind(target)
				: value;
		},
		set(target, prop, value) {
			if (prop === ISOLATED_KEY) {
				tabStorage.setItem(ISOLATED_KEY, String(value));
				return true;
			}
			return Reflect.set(target, prop, value, target);
		},
		deleteProperty(target, prop) {
			if (prop === ISOLATED_KEY) {
				tabStorage.removeItem(ISOLATED_KEY);
				return true;
			}
			return Reflect.deleteProperty(target, prop);
		},
		has(target, prop) {
			if (prop === ISOLATED_KEY) {
				return tabStorage.getItem(ISOLATED_KEY) !== null;
			}
			return Reflect.has(target, prop);
		}
	};

	const proxy = new Proxy(realLocalStorage, handler);
	Object.defineProperty(window, "localStorage", { get: () => proxy, configurable: true });
	win[INSTALLED_FLAG] = true;
}
